/**
 * SEO landing page registry. Each entry powers a route that filters the jobs
 * board to a specific keyword set + renders a long-form SEO page.
 *
 * Adding a new landing page:
 *  1. Append a config object below with a unique `path`.
 *  2. The route is auto-registered in App.tsx via the iteration over LANDING_PAGES.
 *  3. The path is auto-included in the sitemap (sitemaps.auditfriendly.co/sitemap-landings.xml).
 *  4. Add it to the relevant categoryLinks list in `SEOCategoryLinks` if you want internal linking.
 */

import type { FAQItem } from '@/components/seo/SEOFAQSection';

export type LandingPageConfig = {
  path: string;
  heading: string;
  subheading: string;
  titleKeywords: string[];
  alertTitle: string;
  metaTitle: string;
  metaDescription: string;
  category: 'role' | 'specialty' | 'level';
  /** Vestigial: the hero now renders the measured snapshot, never this. */
  avgSalary?: string;
  /** undefined = true. The historic remote-* pages filter to remote; the
   *  root role/specialty pages list everything, matching their
   *  landing_categories snapshot (which was already remote_only=false). */
  remoteOnly?: boolean;
  /** When set, the listing matches jobs by canonical_role equality instead
   *  of title-keyword overlap, the same precision fix the alert engine got:
   *  "senior" in a title should not drag Senior Business Analysts onto the
   *  Senior Accountant page. titleKeywords stays as the search fallback for
   *  jobs whose role never resolved. */
  canonicalRoles?: string[];
};

export const LANDING_PAGES: LandingPageConfig[] = [
  {
    path: '/remote-accounting-jobs',
    heading: 'Remote Accounting Jobs',
    subheading:
      'Find your next work-from-home accounting position. Browse hundreds of remote opportunities for accountants, bookkeepers, and finance professionals.',
    titleKeywords: ['accountant', 'accounting', 'bookkeeper', 'bookkeeping'],
    alertTitle: 'Remote Accountant',
    metaTitle: 'Remote Accounting Jobs, Hand-Curated, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote accounting jobs from top employers. Senior accountant, bookkeeper, controller, CFO, and more, fully remote positions updated every day.',
    category: 'role',
    avgSalary: '$75K',
  },
  {
    path: '/remote-tax-jobs',
    heading: 'Remote Tax Jobs',
    subheading:
      'Discover work-from-home tax positions. Find remote opportunities for tax accountants, tax managers, and tax professionals.',
    titleKeywords: ['tax'],
    alertTitle: 'Remote Tax',
    metaTitle: 'Remote Tax Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote tax jobs from top employers. Tax accountants, tax managers, and tax preparers, fully remote positions updated every day.',
    category: 'specialty',
    avgSalary: '$80K',
  },
  {
    path: '/remote-bookkeeper-jobs',
    heading: 'Remote Bookkeeper Jobs',
    subheading:
      'Find work-from-home bookkeeping positions. Browse remote opportunities for bookkeepers and accounting clerks.',
    titleKeywords: ['bookkeeper', 'bookkeeping'],
    alertTitle: 'Remote Bookkeeper',
    metaTitle: 'Remote Bookkeeper Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote bookkeeper jobs from top employers. Full-charge bookkeepers, accounting clerks, and AP/AR roles, fully remote, updated daily.',
    category: 'role',
    avgSalary: '$55K',
  },
  {
    path: '/remote-controller-jobs',
    heading: 'Remote Controller Jobs',
    subheading:
      'Discover remote controller and assistant controller positions. Find work-from-home opportunities for financial controllers.',
    titleKeywords: ['controller'],
    alertTitle: 'Remote Controller',
    metaTitle: 'Remote Controller Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote controller jobs from top employers. Financial controllers and assistant controllers, fully remote positions updated every day.',
    category: 'role',
    avgSalary: '$125K',
  },
  {
    path: '/remote-senior-accountant-jobs',
    heading: 'Remote Senior Accountant Jobs',
    subheading:
      'Find senior-level remote accounting positions. Browse work-from-home opportunities for experienced accountants.',
    titleKeywords: ['senior accountant', 'sr accountant', 'senior staff accountant'],
    alertTitle: 'Remote Senior Accountant',
    metaTitle: 'Remote Senior Accountant Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote senior accountant jobs from top employers. Experienced accountants, fully remote, new positions updated every day.',
    category: 'level',
    avgSalary: '$90K',
  },
  {
    path: '/work-from-home-accounting-jobs',
    heading: 'Work From Home Accounting Jobs',
    subheading:
      'Browse the latest work from home accounting positions. Find flexible remote opportunities for accounting professionals.',
    titleKeywords: ['accountant', 'accounting', 'bookkeeper', 'bookkeeping'],
    alertTitle: 'Work From Home Accountant',
    metaTitle: 'Work From Home Accounting Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse work-from-home accounting jobs from top employers. Flexible remote positions for accountants and finance professionals, updated daily.',
    category: 'role',
    avgSalary: '$75K',
  },
  {
    path: '/remote-cpa-jobs',
    heading: 'Remote CPA Jobs',
    subheading:
      'Browse remote opportunities for Certified Public Accountants. Senior, manager, and partner-track CPA roles you can do from home.',
    titleKeywords: ['cpa', 'certified public accountant'],
    alertTitle: 'Remote CPA',
    metaTitle: 'Remote CPA Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find remote CPA jobs from top employers. Senior CPA, tax CPA, audit CPA, and management-track roles, fully remote, updated daily.',
    category: 'role',
    avgSalary: '$95K',
  },
  {
    path: '/remote-cfo-jobs',
    heading: 'Remote CFO Jobs',
    subheading:
      'Find remote and fractional CFO positions. Browse Chief Financial Officer opportunities at high-growth startups and established companies.',
    titleKeywords: ['cfo', 'chief financial officer', 'vp finance', 'vp of finance'],
    alertTitle: 'Remote CFO',
    metaTitle: 'Remote CFO Jobs, Fractional & Full-Time | AuditFriendly',
    metaDescription:
      'Browse remote CFO jobs from startups and established companies. Full-time and fractional Chief Financial Officer roles, updated every day.',
    category: 'role',
    avgSalary: '$200K',
  },
  {
    path: '/remote-audit-jobs',
    heading: 'Remote Audit Jobs',
    subheading:
      'Discover remote audit positions at top firms and corporations. Internal audit, external audit, and IT audit roles you can do from anywhere.',
    titleKeywords: ['audit', 'auditor'],
    alertTitle: 'Remote Auditor',
    metaTitle: 'Remote Audit Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find remote audit jobs from Big 4, mid-market firms, and corporations. Internal audit, external audit, IT audit, fully remote, updated daily.',
    category: 'specialty',
    avgSalary: '$85K',
  },
  {
    path: '/remote-accounting-manager-jobs',
    heading: 'Remote Accounting Manager Jobs',
    subheading:
      'Browse remote accounting manager openings. Lead remote accounting teams, oversee monthly close, and manage financial reporting from home.',
    titleKeywords: ['accounting manager', 'manager of accounting'],
    alertTitle: 'Remote Accounting Manager',
    metaTitle: 'Remote Accounting Manager Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote accounting manager jobs from top employers. Lead month-end close, manage teams, oversee reporting, fully remote, updated daily.',
    category: 'level',
    avgSalary: '$115K',
  },
  {
    path: '/remote-financial-analyst-jobs',
    heading: 'Remote Financial Analyst Jobs',
    subheading:
      'Find remote FP&A and financial analyst positions. Build models, drive insights, and support strategic decisions from home.',
    titleKeywords: ['financial analyst', 'fp&a', 'fpa', 'finance analyst'],
    alertTitle: 'Remote Financial Analyst',
    metaTitle: 'Remote Financial Analyst Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote financial analyst and FP&A jobs from top employers. Modeling, forecasting, and strategic finance roles, fully remote, updated daily.',
    category: 'role',
    avgSalary: '$80K',
  },
  {
    path: '/remote-staff-accountant-jobs',
    heading: 'Remote Staff Accountant Jobs',
    subheading:
      'Browse entry-level and mid-level remote staff accountant positions. Build your accounting career from anywhere.',
    titleKeywords: ['staff accountant'],
    alertTitle: 'Remote Staff Accountant',
    metaTitle: 'Remote Staff Accountant Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find remote staff accountant jobs from top employers. Entry-level and mid-level accounting roles you can do from home, updated daily.',
    category: 'level',
    avgSalary: '$60K',
  },
  {
    path: '/remote-payroll-jobs',
    heading: 'Remote Payroll Jobs',
    subheading:
      'Discover remote payroll specialist and payroll manager positions. Process payroll, manage compliance, and run benefits from home.',
    titleKeywords: ['payroll'],
    alertTitle: 'Remote Payroll',
    metaTitle: 'Remote Payroll Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote payroll jobs from top employers. Payroll specialists, payroll managers, and payroll analysts, fully remote, updated daily.',
    category: 'specialty',
    avgSalary: '$65K',
  },
  {
    path: '/remote-accounts-payable-jobs',
    heading: 'Remote Accounts Payable Jobs',
    subheading:
      'Find remote AP clerk, AP specialist, and AP manager positions. Process invoices and manage vendor payments from home.',
    titleKeywords: ['accounts payable', 'ap clerk', 'ap specialist', 'ap manager'],
    alertTitle: 'Remote Accounts Payable',
    metaTitle: 'Remote Accounts Payable Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Browse remote accounts payable jobs from top employers. AP clerks, specialists, and managers, fully remote, updated daily.',
    category: 'specialty',
    avgSalary: '$55K',
  },
  {
    path: '/remote-accounts-receivable-jobs',
    heading: 'Remote Accounts Receivable Jobs',
    subheading:
      'Browse remote AR clerk, AR specialist, and collections positions. Manage receivables and customer accounts from home.',
    titleKeywords: ['accounts receivable', 'ar clerk', 'ar specialist', 'collections'],
    alertTitle: 'Remote Accounts Receivable',
    metaTitle: 'Remote Accounts Receivable Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find remote accounts receivable jobs from top employers. AR clerks, specialists, and collections roles, fully remote, updated daily.',
    category: 'specialty',
    avgSalary: '$55K',
  },
  {
    path: '/entry-level-remote-accounting-jobs',
    heading: 'Entry-Level Remote Accounting Jobs',
    subheading:
      'Start your accounting career from home. Browse entry-level and junior remote accounting positions perfect for new grads and career changers.',
    titleKeywords: ['junior accountant', 'entry level', 'accounting clerk', 'staff accountant'],
    alertTitle: 'Entry-Level Remote Accountant',
    metaTitle: 'Entry-Level Remote Accounting Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find entry-level remote accounting jobs perfect for new grads. Junior accountants, accounting clerks, and staff accountants, fully remote, updated daily.',
    category: 'level',
    avgSalary: '$50K',
  },
  {
    path: '/remote-finance-jobs',
    heading: 'Remote Finance Jobs',
    subheading:
      'Browse remote finance positions across all roles. Financial analysts, treasury, FP&A, and corporate finance, fully remote.',
    titleKeywords: ['finance', 'financial', 'treasury'],
    alertTitle: 'Remote Finance',
    metaTitle: 'Remote Finance Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find remote finance jobs from top employers. FP&A, treasury, corporate finance, and financial analyst roles, fully remote, updated daily.',
    category: 'role',
    avgSalary: '$85K',
  },
  // Bare-prefix umbrella landings, intentionally broader than the /remote-* siblings
  // (all locations + all employment types) so they don't compete as duplicate content.
  // MUST stay in sync with cloudflare-worker.js LANDING_PAGES.
  {
    path: '/cpa-jobs',
    remoteOnly: false,
    heading: 'CPA Jobs',
    subheading:
      'Browse Certified Public Accountant openings across the US and Canada. Public accounting, industry, tax, audit, and advisory, on-site, hybrid, and remote CPA roles updated daily.',
    titleKeywords: ['cpa', 'certified public accountant'],
    alertTitle: 'CPA',
    metaTitle: 'CPA Jobs, Public Accounting, Industry & Remote | AuditFriendly',
    metaDescription:
      'Find CPA jobs from Big 4, mid-market firms, and Fortune 500 employers. Senior CPA, tax CPA, audit CPA, and manager-track roles across all locations, updated daily.',
    category: 'role',
    avgSalary: '$95K',
  },
  {
    path: '/cfo-jobs',
    remoteOnly: false,
    heading: 'CFO Jobs',
    subheading:
      'Discover Chief Financial Officer openings at startups, growth-stage companies, and established enterprises. Full-time, fractional, interim, and remote CFO roles in one place.',
    titleKeywords: ['cfo', 'chief financial officer', 'vp finance', 'vp of finance'],
    alertTitle: 'CFO',
    metaTitle: 'CFO Jobs, Full-Time, Fractional & Interim | AuditFriendly',
    metaDescription:
      'Browse CFO jobs across startups, scale-ups, and Fortune 500 companies. Full-time, fractional, and interim Chief Financial Officer roles, all locations, updated daily.',
    category: 'role',
    avgSalary: '$200K',
  },
  {
    path: '/audit-jobs',
    remoteOnly: false,
    heading: 'Audit Jobs',
    subheading:
      'Explore audit careers across public accounting, internal audit, IT audit, and SOX compliance. Big 4, mid-market firms, and in-house audit teams hiring across all locations.',
    titleKeywords: ['audit', 'auditor'],
    alertTitle: 'Auditor',
    metaTitle: 'Audit Jobs, Internal, External & IT Audit | AuditFriendly',
    metaDescription:
      'Find audit jobs from Big 4, mid-market firms, and corporate audit teams. Internal audit, external audit, IT audit, and SOX roles, all locations, updated daily.',
    category: 'specialty',
    avgSalary: '$85K',
  },
  {
    path: '/payroll-jobs',
    remoteOnly: false,
    heading: 'Payroll Jobs',
    subheading:
      'Browse payroll specialist, payroll manager, and payroll analyst openings. Multi-state, multi-country, and equity payroll roles across on-site, hybrid, and remote setups.',
    titleKeywords: ['payroll'],
    alertTitle: 'Payroll',
    metaTitle: 'Payroll Jobs, Specialists, Managers & Analysts | AuditFriendly',
    metaDescription:
      'Browse payroll jobs from top employers. Payroll specialists, payroll managers, payroll analysts, and multi-state payroll roles, all locations, updated daily.',
    category: 'specialty',
    avgSalary: '$65K',
  },
  {
    path: '/finance-jobs',
    remoteOnly: false,
    heading: 'Finance Jobs',
    subheading:
      'Discover finance careers across FP&A, treasury, corporate finance, investor relations, and strategic finance. Roles at every level, in every location, updated daily.',
    titleKeywords: ['finance', 'financial', 'treasury'],
    alertTitle: 'Finance',
    metaTitle: 'Finance Jobs, FP&A, Treasury & Corporate Finance | AuditFriendly',
    metaDescription:
      'Find finance jobs from top employers. FP&A, treasury, corporate finance, investor relations, and strategic finance roles across all locations, updated daily.',
    category: 'role',
    avgSalary: '$85K',
  },
  {
    path: '/entry-level-jobs',
    remoteOnly: false,
    heading: 'Entry-Level Accounting & Finance Jobs',
    subheading:
      'Launch your accounting or finance career. Browse entry-level staff accountant, junior analyst, AP/AR clerk, and audit associate roles for new grads and career changers, all locations.',
    titleKeywords: ['junior', 'entry level', 'staff accountant', 'accounting clerk', 'associate'],
    alertTitle: 'Entry-Level Accounting',
    metaTitle: 'Entry-Level Accounting & Finance Jobs | AuditFriendly',
    metaDescription:
      'Find entry-level accounting and finance jobs perfect for new grads. Junior accountants, staff accountants, audit associates, and AP/AR clerks across all locations, updated daily.',
    category: 'level',
    avgSalary: '$55K',
  },
  // ── role pages, canonical-role matched ──────────────────────────────────
  // Inventory-gated: every role here had well over 100 live postings when
  // added (staff accountant 4,099, controller+assistant 3,100+, and so on).
  // These match by canonical_role, not title keywords, so the Senior
  // Accountant page cannot pick up a Senior Business Analyst.
  {
    path: '/staff-accountant-jobs',
    remoteOnly: false,
    canonicalRoles: ['staff_accountant'],
    heading: 'Staff Accountant Jobs',
    subheading:
      'Browse live staff accountant openings across public accounting and industry, on-site, hybrid and remote. Every listing is classified by role, so this page shows staff accountant work and nothing else.',
    titleKeywords: ['staff accountant'],
    alertTitle: 'Staff Accountant',
    metaTitle: 'Staff Accountant Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find staff accountant jobs from top employers. On-site, hybrid and remote staff accountant roles with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/senior-accountant-jobs',
    remoteOnly: false,
    canonicalRoles: ['senior_accountant'],
    heading: 'Senior Accountant Jobs',
    subheading:
      'Live senior accountant openings, matched by role rather than by the word "senior", so senior analyst and senior manager listings stay off this page.',
    titleKeywords: ['senior accountant', 'sr accountant'],
    alertTitle: 'Senior Accountant',
    metaTitle: 'Senior Accountant Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find senior accountant jobs from top employers. On-site, hybrid and remote senior accountant roles with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/controller-jobs',
    remoteOnly: false,
    canonicalRoles: ['controller', 'assistant_controller', 'senior_controller'],
    heading: 'Controller Jobs',
    subheading:
      'Financial controller, assistant controller and senior controller openings across every industry, with measured advertised pay on this page.',
    titleKeywords: ['controller'],
    alertTitle: 'Controller',
    metaTitle: 'Controller Jobs, Financial & Assistant Controller | AuditFriendly',
    metaDescription:
      'Find controller jobs from top employers. Financial controllers, assistant controllers and senior controllers, all locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/financial-analyst-jobs',
    remoteOnly: false,
    canonicalRoles: ['financial_analyst', 'senior_financial_analyst'],
    heading: 'Financial Analyst Jobs',
    subheading:
      'Financial analyst and senior financial analyst openings, matched by role. Modeling, forecasting and reporting roles at every level.',
    titleKeywords: ['financial analyst'],
    alertTitle: 'Financial Analyst',
    metaTitle: 'Financial Analyst Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find financial analyst jobs from top employers. Analyst and senior analyst roles across all industries and locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/accounting-manager-jobs',
    remoteOnly: false,
    canonicalRoles: ['accounting_manager', 'senior_accounting_manager'],
    heading: 'Accounting Manager Jobs',
    subheading:
      'Accounting manager and senior accounting manager openings: lead month-end close, manage teams and own the reporting cycle.',
    titleKeywords: ['accounting manager'],
    alertTitle: 'Accounting Manager',
    metaTitle: 'Accounting Manager Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find accounting manager jobs from top employers. Manager and senior manager roles across all industries and locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/tax-manager-jobs',
    remoteOnly: false,
    canonicalRoles: ['tax_manager', 'tax_senior_manager'],
    heading: 'Tax Manager Jobs',
    subheading:
      'Tax manager and tax senior manager openings across public accounting and industry, matched by role rather than by the word "tax".',
    titleKeywords: ['tax manager'],
    alertTitle: 'Tax Manager',
    metaTitle: 'Tax Manager Jobs, Public & Industry | AuditFriendly',
    metaDescription:
      'Find tax manager jobs at CPA firms and in industry. Tax manager and senior manager roles, all locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/accounts-payable-jobs',
    remoteOnly: false,
    canonicalRoles: ['accounts_payable_specialist', 'accounts_payable_clerk', 'accounts_payable_manager'],
    heading: 'Accounts Payable Jobs',
    subheading:
      'Accounts payable clerk, specialist and manager openings, the full AP ladder on one page, matched by role.',
    titleKeywords: ['accounts payable'],
    alertTitle: 'Accounts Payable',
    metaTitle: 'Accounts Payable Jobs, Clerk to Manager | AuditFriendly',
    metaDescription:
      'Find accounts payable jobs from top employers. AP clerks, specialists and managers, all locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/accounts-receivable-jobs',
    remoteOnly: false,
    canonicalRoles: ['accounts_receivable_specialist', 'accounts_receivable_manager'],
    heading: 'Accounts Receivable Jobs',
    subheading:
      'Accounts receivable specialist and manager openings, billing and collections included, matched by role.',
    titleKeywords: ['accounts receivable'],
    alertTitle: 'Accounts Receivable',
    metaTitle: 'Accounts Receivable Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find accounts receivable jobs from top employers. AR specialists and managers, all locations, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/bookkeeper-jobs',
    remoteOnly: false,
    canonicalRoles: ['bookkeeper', 'senior_bookkeeper'],
    heading: 'Bookkeeper Jobs',
    subheading:
      'Bookkeeper and senior bookkeeper openings, on-site and remote, from small firms to outsourced accounting providers.',
    titleKeywords: ['bookkeeper', 'bookkeeping'],
    alertTitle: 'Bookkeeper',
    metaTitle: 'Bookkeeper Jobs, Updated Daily | AuditFriendly',
    metaDescription:
      'Find bookkeeper jobs from top employers. Full-charge and senior bookkeeper roles, on-site and remote, with measured pay data, updated daily.',
    category: 'role',
  },
  {
    path: '/fpa-jobs',
    remoteOnly: false,
    canonicalRoles: ['fpa_analyst', 'senior_fpa_analyst', 'fpa_manager', 'fpa_director'],
    heading: 'FP&A Jobs',
    subheading:
      'FP&A analyst through FP&A director, the whole planning-and-analysis ladder on one page. See what each rung pays on our FP&A ladder dataset.',
    titleKeywords: ['fp&a', 'fpa', 'financial planning'],
    alertTitle: 'FP&A',
    metaTitle: 'FP&A Jobs, Analyst to Director | AuditFriendly',
    metaDescription:
      'Find FP&A jobs from top employers. FP&A analyst, manager and director roles across all industries, with measured pay data, updated daily.',
    category: 'specialty',
  },
  {
    path: '/tax-jobs',
    remoteOnly: false,
    heading: 'Tax Jobs',
    subheading:
      'Tax roles across public accounting and industry: associates, seniors, managers and directors, federal, state, indirect and international.',
    titleKeywords: ['tax'],
    alertTitle: 'Tax',
    metaTitle: 'Tax Jobs, Public Accounting & Industry | AuditFriendly',
    metaDescription:
      'Find tax jobs at CPA firms and in industry. Tax associate through tax director, all specialties and locations, updated daily.',
    category: 'specialty',
  },
];

export function getLandingConfig(pathname: string): LandingPageConfig | null {
  return LANDING_PAGES.find(p => p.path === pathname) ?? null;
}

export const LANDING_PATHS: readonly string[] = LANDING_PAGES.map(p => p.path);

/** The pages that render in the Data Desk layout rather than the job-board
 *  template: the canonical-role pages added 2026-08-27. The older remote-*
 *  landings keep the job-board look because they carry existing rankings and
 *  changing them is a separate decision. */
/**
 * Which landing paths render the Data Desk layout (src/pages/data/RolePage.tsx).
 *
 * Now every one of them. The role pages shipped on the Data Desk layout first
 * and the legacy remote-* pages kept the old job-board template, which left
 * two different looks across one family of pages. The remote pages carry
 * existing rankings, so the change was made deliberately and only once the
 * layout had proven out: URLs, titles, meta and H1s are untouched, and the
 * content each page carries goes UP (measured pay band, an 8-month trend,
 * above-market roles, employers, metros, skills) in place of hand-written
 * "why choose remote accounting" blocks.
 *
 * landing_page_data already covers all 34 paths, so this is routing only.
 */
export const ROLE_PAGE_PATHS: ReadonlySet<string> = new Set(LANDING_PAGES.map((p) => p.path));


export const SHARED_FAQS: FAQItem[] = [
  {
    question: 'What qualifications do I need for remote accounting jobs?',
    answer:
      "Most remote accounting positions require a bachelor's degree in accounting, finance, or a related field. Many employers prefer candidates with CPA certification, especially for senior roles. Experience with accounting software like QuickBooks, Xero, or enterprise ERP systems is often required. Strong communication skills are essential for remote work.",
  },
  {
    question: 'How much do remote accountants earn?',
    answer:
      'Pay depends on experience, certifications, and the role itself, so we measure it instead of guessing: the market snapshot on this page shows advertised pay percentiles computed from live postings that disclose a range, refreshed twice daily. Postings without a disclosed range are excluded rather than estimated, and CPA certification typically moves a candidate up the range.',
  },
  {
    question: 'What software skills are required for remote accounting work?',
    answer:
      'Essential software skills include proficiency in Excel/Google Sheets, accounting software (QuickBooks, Xero, Sage, NetSuite), and ERP systems. Familiarity with cloud-based tools like Bill.com, Expensify, and collaboration platforms (Slack, Microsoft Teams, Zoom) is increasingly important. Many roles also require experience with data visualization tools and basic SQL knowledge.',
  },
  {
    question: 'Are CPA credentials required for remote accounting jobs?',
    answer:
      "CPA certification is not always required but significantly increases job opportunities and earning potential. Many entry-level and staff accountant positions don't require CPA. However, roles involving auditing, tax preparation, or senior financial positions typically require or strongly prefer CPA certification. Remote CPA jobs often command 10-20% higher salaries.",
  },
  {
    question: 'What types of companies hire remote accountants?',
    answer:
      'Remote accountants are hired across all industries including tech startups, e-commerce companies, professional services firms, healthcare organizations, and nonprofits. Many public accounting firms now offer remote options. Outsourced accounting firms specifically hire remote professionals, and many companies use remote accountants for contract or fractional work.',
  },
  {
    question: 'How do I find legitimate remote accounting jobs?',
    answer:
      'Use specialized job boards like this one that focus on accounting and finance roles. Verify company legitimacy through LinkedIn, Glassdoor reviews, and official company websites. Be wary of positions asking for upfront payments or personal financial information. Look for roles posted directly by companies or reputable recruiting firms.',
  },
];
